// Business stage — pre-launch vs. ongoing.
//
// The user's decision on this is captured in the wizard as `timeframe`.
// A value that starts with "Already operating" means the founder is
// running the business today (an "ongoing" business); every other
// timeframe is a "pre-launch" business preparing to open the doors.
//
// This helper centralises the check so every surface (Journey, badges,
// Admin Reports, PDFs) infers stage from the same signal and never
// diverges.
//
// It also exposes a `shouldSuggestOngoing()` helper that lets us prompt
// pre-launch founders who look like they've already launched (revenue
// actuals, high roadmap completion, `operating_since` set) to flip
// themselves to Ongoing — closes the gap between what the founder
// declared and what the data says.

export const STAGE_PRE_LAUNCH = "pre_launch";
export const STAGE_ONGOING = "ongoing";
export const STAGE_OPERATING = "operating";

export function isOngoing(business) {
  const tf = String(business?.questionnaire?.timeframe || "").trim().toLowerCase();
  return tf.startsWith("already operating");
}

// Feb 18 2026 · Phase 1 · Launch → Operate transition.
// Once a founder graduates the plan (`business.mode === "operating"`),
// they've officially committed to running the business — a stronger
// signal than the timeframe-derived `isOngoing()`. Every surface that
// shows a stage indicator (badge, ribbon, PDFs) should prefer this.
export function isOperatingMode(business) {
  return (business?.mode || "launching") === "operating";
}

export function stageKey(business) {
  if (isOperatingMode(business)) return STAGE_OPERATING;
  return isOngoing(business) ? STAGE_ONGOING : STAGE_PRE_LAUNCH;
}

export function stageLabel(business) {
  if (isOperatingMode(business)) return "Operating";
  return isOngoing(business) ? "Ongoing" : "Pre-Launch";
}

// Jul 2026 · When this plan moved to Ongoing/Operating. Prefers the explicit
// timestamps the two flip paths record — `operating_since` (Mark-as-operating)
// and `entered_operating_at` (mode graduation) — falling back to `launched_at`.
// Returns a Date, or null for pre-launch plans / when nothing was recorded.
export function operatingSince(business) {
  if (!isOperatingMode(business) && !isOngoing(business)) return null;
  const raw = business?.operating_since || business?.entered_operating_at || business?.launched_at;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Human "how long operating" label, e.g. "today", "3 weeks", "5 months", "2y 3m".
export function operatingDurationLabel(business, now = new Date()) {
  const since = operatingSince(business);
  if (!since) return null;
  const days = Math.max(0, Math.floor((now - since) / 86400000));
  if (days < 1) return "today";
  if (days < 14) return `${days} day${days === 1 ? "" : "s"}`;
  if (days < 60) { const w = Math.floor(days / 7); return `${w} week${w === 1 ? "" : "s"}`; }
  if (days < 365) { const m = Math.floor(days / 30); return `${m} month${m === 1 ? "" : "s"}`; }
  const years = Math.floor(days / 365);
  const remMonths = Math.floor((days - years * 365) / 30);
  return remMonths > 0 ? `${years}y ${remMonths}m` : `${years} year${years === 1 ? "" : "s"}`;
}

// Signals that a pre-launch founder is behaving like an operating one.
// Any of:
//   • `operating_since` set (they clicked Mark as operating already —
//     but timeframe somehow didn't flip — legacy data safety net).
//   • Any month of revenue actuals recorded.
//   • Roadmap >= 60% done AND has at least 3 done items (matches the
//     server-side sanity floor in `POST /businesses/:id/mark-operating`).
export function shouldSuggestOngoing(business) {
  if (!business || isOngoing(business)) return false;
  if (business.operating_since) return true;
  const rev = business.budget_revenue_actuals || {};
  const hasRevActuals = Object.values(rev).some((row) => {
    if (!row || typeof row !== "object") return false;
    return Object.values(row).some((v) => Number(v) > 0);
  });
  if (hasRevActuals) return true;
  const items = Array.isArray(business.roadmap) ? business.roadmap : [];
  const done = items.filter((r) => String(r?.status || "").toLowerCase() === "done").length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  if (done >= 3 && pct >= 60) return true;
  return false;
}
